import React from 'react';
import {
  Book,
  Brain,
  Globe,
  Headphones,
  Lightbulb,
  Loader2,
  MessageSquare,
  Plus,
  Sparkles,
  Trash2,
  Video,
  Zap,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';

const categories = [
  { id: 'websites', name: 'Websites', icon: Globe },
  { id: 'podcasts', name: 'Podcasts', icon: Headphones },
  { id: 'books', name: 'Books', icon: Book },
  { id: 'videos', name: 'Videos', icon: Video },
  { id: 'curiosities', name: 'Curiosities', icon: Sparkles }
];

const getResourceClicks = (resource = {}) => {
  const rawValue = resource?.click_count ?? resource?.clicks ?? 0;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : 0;
};

const ResourcesTab = ({
  newResource,
  setNewResource,
  adding,
  analyzingResources,
  resourceInsights,
  resources,
  loading,
  editingResource,
  setEditingResource,
  editForm,
  setEditForm,
  handleAddResource,
  handleStartEdit,
  handleSaveEdit,
  handleDeleteResource,
  runResourceAnalysis,
  activeZone,
  onOpenTool,
}) => {
  const rankedResources = [...resources].sort((a, b) => {
    const clickDiff = getResourceClicks(b) - getResourceClicks(a);
    if (clickDiff !== 0) return clickDiff;
    return (a?.name || '').localeCompare(b?.name || '');
  });
  const topResource = rankedResources[0] || null;
  const activeResourcesCount = rankedResources.filter((resource) => getResourceClicks(resource) > 0).length;
  const totalClicks = rankedResources.reduce((sum, resource) => sum + getResourceClicks(resource), 0);
  const resourceGapCount = Array.isArray(resourceInsights?.gaps) ? resourceInsights.gaps.length : 0;

  return (
    <div className="space-y-6">
      <Card className="border-emerald-200 bg-gradient-to-br from-[#eef9f1] via-white to-[#eef6f2] p-6 dark:border-emerald-500/15 dark:bg-gradient-to-br dark:from-[#122018] dark:via-[#11161a] dark:to-[#101513]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-emerald-700 dark:text-emerald-300">
              {activeZone?.id === 'heart-work' ? 'Dad Partners flow' : 'Resources flow'}
            </p>
            <h3 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              Keep the library tied to real support gaps and what dads already use
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
              This tool works best when it helps you see two things at once: what is already landing with dads, and what support is still missing. Add or refine resources here only after the need is clear enough to name.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenTool?.('feedback', 'listen-learn')}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Review feedback
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenTool?.('recordings', 'support-dads')}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                <Video className="mr-2 h-4 w-4" />
                Open recordings
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={runResourceAnalysis}
                disabled={analyzingResources}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                {analyzingResources ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Brain className="mr-2 h-4 w-4" />}
                {analyzingResources ? 'Analyzing...' : 'Analyze gaps'}
              </Button>
            </div>
          </div>

          <div className="grid min-w-full gap-3 sm:grid-cols-2 xl:min-w-[380px] xl:max-w-[460px]">
            <div className="rounded-2xl border border-emerald-200 bg-white/90 p-4 dark:border-emerald-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Library size</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{resources.length}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Resources currently available to support dads</p>
            </div>
            <div className="rounded-2xl border border-cyan-200 bg-white/90 p-4 dark:border-cyan-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Getting traction</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{activeResourcesCount}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Resources with at least one click from dads</p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-white/90 p-4 dark:border-amber-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Top landing now</p>
              <p className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{topResource?.name || 'No signal yet'}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">
                {topResource ? `${getResourceClicks(topResource)} clicks across the current library` : 'Run analysis or wait for dads to engage with the library'}
              </p>
            </div>
            <div className="rounded-2xl border border-red-200 bg-white/90 p-4 dark:border-red-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Current gaps</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{resourceGapCount}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Support gaps named by the latest analysis</p>
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-2xl border border-emerald-200 bg-white/80 p-4 dark:border-emerald-500/20 dark:bg-black/20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-300">
              What dads are already using
            </p>
            <div className="mt-3 space-y-3">
              {rankedResources.slice(0, 3).map((resource) => (
                <div key={resource.resource_id || resource.id || resource.name} className="rounded-xl border border-slate-200 bg-white/80 p-3 dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{resource.name}</p>
                      <p className="mt-1 text-sm text-slate-600 dark:text-gray-300">{resource.category}</p>
                    </div>
                    <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                      {getResourceClicks(resource)} clicks
                    </Badge>
                  </div>
                </div>
              ))}
              {rankedResources.length === 0 ? (
                <p className="text-sm text-slate-600 dark:text-gray-300">No resources are loaded yet, so this library is still waiting for its first support signal.</p>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 dark:border-white/10 dark:bg-black/20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">
              Analysis in view
            </p>
            {resourceInsights ? (
              <div className="mt-3 space-y-3">
                <p className="text-sm text-slate-700 dark:text-gray-300">
                  {resourceInsights.topClicked || `${totalClicks} total clicks are currently visible across the library.`}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(resourceInsights.gaps || []).slice(0, 4).map((gap, index) => (
                    <Badge key={`${gap}-${index}`} className="border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300">
                      {gap}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                Run the gap analysis when you want a sharper read on what the library is missing for dads right now.
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-aqua" />
          Add New Resource
        </h3>
        <p className="mb-4 text-sm text-gray-400">
          Add a resource when it clearly answers a real need dads are naming, not just because the library feels sparse.
        </p>
        <form onSubmit={handleAddResource} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Category *</label>
              <select
                value={newResource.category}
                onChange={(e) => setNewResource({ ...newResource, category: e.target.value })}
                className="w-full bg-[#16161a] border border-[#2a2a32] rounded-lg px-4 py-3 text-white"
              >
                <option value="">Select category...</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Name *</label>
              <Input value={newResource.name} onChange={(e) => setNewResource({ ...newResource, name: e.target.value })} placeholder="Resource name" className="bg-[#16161a] border-[#2a2a32] text-white" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Media Type</label>
              <select
                value={newResource.mediaType}
                onChange={(e) => setNewResource({ ...newResource, mediaType: e.target.value })}
                className="w-full bg-[#16161a] border border-[#2a2a32] rounded-lg px-4 py-3 text-white"
              >
                <option value="">Select type...</option>
                <option value="video">Video</option>
                <option value="podcast">Podcast</option>
                <option value="article">Article</option>
                <option value="book">Book</option>
                <option value="course">Course</option>
                <option value="tool">Tool/App</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">URL</label>
              <Input type="url" value={newResource.url} onChange={(e) => setNewResource({ ...newResource, url: e.target.value })} placeholder="https://..." className="bg-[#16161a] border-[#2a2a32] text-white" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Author</label>
              <Input value={newResource.author} onChange={(e) => setNewResource({ ...newResource, author: e.target.value })} placeholder="Author name" className="bg-[#16161a] border-[#2a2a32] text-white" />
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <Input value={newResource.description} onChange={(e) => setNewResource({ ...newResource, description: e.target.value })} placeholder="Brief description" className="bg-[#16161a] border-[#2a2a32] text-white" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Amazon URL</label>
              <Input type="url" value={newResource.amazonUrl} onChange={(e) => setNewResource({ ...newResource, amazonUrl: e.target.value })} placeholder="https://www.amazon.com/..." className="bg-[#16161a] border-[#2a2a32] text-white" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Recommendation Note</label>
              <Input value={newResource.recommendationNote} onChange={(e) => setNewResource({ ...newResource, recommendationNote: e.target.value })} placeholder="Why this helps dads" className="bg-[#16161a] border-[#2a2a32] text-white" />
            </div>
          </div>
          <Button type="submit" disabled={adding} className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Resource
          </Button>
        </form>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Manage Resources</h3>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-aqua animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {rankedResources.map((res) => (
              <div key={res.resource_id || res.id} className="flex items-center justify-between p-4 bg-[#16161a] rounded-xl">
                <div>
                  <p className="text-white font-medium">{res.name}</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <Badge className="border-slate-500/30 bg-slate-500/10 text-slate-300">{res.category}</Badge>
                    {res.media_type ? (
                      <Badge className="border-aqua/30 bg-aqua/10 text-aqua">{res.media_type}</Badge>
                    ) : null}
                  </div>
                  {res.url && <p className="text-aqua text-xs truncate max-w-xs">{res.url}</p>}
                  {res.amazon_url && <p className="text-yellow-400 text-xs truncate max-w-xs">{res.amazon_url}</p>}
                  {res.recommendation_note && <p className="text-gray-400 text-xs mt-1 max-w-md">{res.recommendation_note}</p>}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-400 text-sm">{res.click_count || res.clicks || 0} clicks</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#2a2a32] text-gray-400 hover:text-white"
                    onClick={() => handleStartEdit(res)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDeleteResource(res.resource_id || res.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editingResource && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-white mb-4">Edit Resource</h3>
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm">Name</label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">URL</label>
                <Input
                  value={editForm.url}
                  onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                  placeholder="https://..."
                  className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Author</label>
                <Input
                  value={editForm.author}
                  onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                  className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Description</label>
                <Input
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Brief description"
                  className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Amazon URL</label>
                <Input
                  value={editForm.amazonUrl}
                  onChange={(e) => setEditForm({ ...editForm, amazonUrl: e.target.value })}
                  placeholder="https://www.amazon.com/..."
                  className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm">Recommendation Note</label>
                <Input
                  value={editForm.recommendationNote}
                  onChange={(e) => setEditForm({ ...editForm, recommendationNote: e.target.value })}
                  placeholder="Why this helps dads"
                  className="bg-[#16161a] border-[#2a2a32] text-white mt-1"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setEditingResource(null)}
                  className="flex-1 border-[#2a2a32] text-white hover:bg-[#16161a]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  className="flex-1 bg-aqua hover:bg-aqua-dark text-charcoal"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Brain className="w-5 h-5 text-aqua" />
            AI Resource Analysis
          </h3>
          <Button onClick={runResourceAnalysis} disabled={analyzingResources} className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2">
            {analyzingResources ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Analyze
          </Button>
        </div>

        {resourceInsights ? (
          <div className="space-y-4">
            <div className="p-4 bg-aqua/10 border border-aqua/20 rounded-xl">
              <p className="text-aqua font-medium">Top Performer:</p>
              <p className="text-white">{resourceInsights.topClicked}</p>
            </div>
            <div>
              <p className="text-white font-medium mb-2">Recommended Additions:</p>
              <ul className="space-y-2">
                {resourceInsights.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-400 text-sm">
                    <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-white font-medium mb-2">Content Gaps:</p>
              <div className="flex flex-wrap gap-2">
                {resourceInsights.gaps.map((g, i) => (
                  <Badge key={i} className="bg-red-500/10 text-red-400">{g}</Badge>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Run analysis to get resource recommendations</p>
        )}
      </Card>
    </div>
  );
};

export default ResourcesTab;
